/**
 * Level7Scene - 关卡7：磁场与洛伦兹力
 * @description 教学目标：理解洛伦兹力 F = qv×B
 * 玩家通过调节磁场强度和初速度，观察带电粒子在磁场中的偏转运动
 */

import Phaser from 'phaser';
import { Vec2 } from 'planck';
import type { LevelConfig } from '@/levels/LevelConfig.ts';
import { TutorialUI } from '@/ui/TutorialUI.ts';
import { getTutorial } from '@/data/tutorials.ts';
import { getGameState } from '@/core/GameState.ts';
import { EMField, type ChargedParticle, type MagneticSource } from '@/physics/EMField.ts';
import { FieldRenderer } from '@/physics/FieldRenderer.ts';

// 关卡7特定配置
interface Level7SpecificConfig {
  magneticFieldStrength: number;
  particleCharge: number;
  particleMass: number;
  initialVelocity: number;
  formula: string;
  hint: string;
  magneticSources: MagneticSource[];
}

// 游戏状态
type GameState = 'idle' | 'running' | 'success' | 'failed';

// 游戏尺寸常量
const GAME_WIDTH = 800;
const GAME_HEIGHT = 600;

// 粒子轨迹历史点
interface TrajectoryPoint {
  x: number;
  y: number;
}

/**
 * Level7Scene 类
 * 关卡7场景，实现洛伦兹力教学
 */
export class Level7Scene extends Phaser.Scene {
  private levelConfig: LevelConfig | null = null;
  private levelSpecific: Level7SpecificConfig | null = null;

  private gameState: GameState = 'idle';
  private elapsedTime: number = 0;

  private emField: EMField;
  private fieldRenderer: FieldRenderer | null = null;

  private particle: ChargedParticle | null = null;
  private particleGraphics: Phaser.GameObjects.Graphics | null = null;
  private trajectoryGraphics: Phaser.GameObjects.Graphics | null = null;
  private trajectoryHistory: TrajectoryPoint[] = [];

  private goalZone: Phaser.GameObjects.Graphics | null = null;

  private hintText: Phaser.GameObjects.Text | null = null;
  private velocityInput: Phaser.GameObjects.Text | null = null;
  private fieldStrengthInput: Phaser.GameObjects.Text | null = null;
  private timerText: Phaser.GameObjects.Text | null = null;
  private startButton: Phaser.GameObjects.Graphics | null = null;
  private resultText: Phaser.GameObjects.Text | null = null;

  private velocityValue: number = 100;
  private fieldStrengthValue: number = 1.0;

  private tutorialUI: TutorialUI | null = null;

  constructor() {
    super({ key: 'Level7Scene' });
    this.emField = new EMField(1e-4);
  }

  preload(): void {
    this.load.json('level-7', 'src/levels/levels/level-7.json');
  }

  create(): void {
    this.levelConfig = this.cache.json.get('level-7') as LevelConfig;

    if (this.levelConfig && this.levelConfig.levelSpecific) {
      this.levelSpecific = this.levelConfig.levelSpecific as unknown as Level7SpecificConfig;
      this.velocityValue = this.levelSpecific.initialVelocity ?? 100;
      this.fieldStrengthValue = this.levelSpecific.magneticFieldStrength ?? 1.0;
    }

    this.createBackground();
    this.createGameScene();
    this.createUI();
    this.setupInputEvents();
    this.showTutorial();
  }

  override update(_time: number, delta: number): void {
    if (this.gameState !== 'running') return;

    this.elapsedTime += delta / 1000;
    this.updateTimerDisplay();

    const dt = delta / 1000;
    this.updateParticlePhysics(dt);
    this.renderParticle();
    this.drawTrajectory();
    this.checkGameEnd();
  }

  private createBackground(): void {
    this.cameras.main.setBackgroundColor('#1a1a2e');

    const gridGraphics = this.add.graphics();
    gridGraphics.lineStyle(1, 0x2a2a4a, 0.3);

    for (let x = 0; x <= GAME_WIDTH; x += 50) {
      gridGraphics.beginPath();
      gridGraphics.moveTo(x, 0);
      gridGraphics.lineTo(x, GAME_HEIGHT);
      gridGraphics.strokePath();
    }

    for (let y = 0; y <= GAME_HEIGHT; y += 50) {
      gridGraphics.beginPath();
      gridGraphics.moveTo(0, y);
      gridGraphics.lineTo(GAME_WIDTH, y);
      gridGraphics.strokePath();
    }
  }

  private createGameScene(): void {
    this.initializeMagneticField();
    this.createFieldRenderer();

    this.goalZone = this.add.graphics();
    this.goalZone.fillStyle(0x4ade80, 0.3);
    this.goalZone.fillRect(620, 270, 60, 60);
    this.goalZone.lineStyle(3, 0x4ade80);
    this.goalZone.strokeRect(620, 270, 60, 60);

    this.add.text(650, 340, '目标', {
      fontSize: '16px',
      color: '#4ade80',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    this.trajectoryGraphics = this.add.graphics();
    this.trajectoryGraphics.setDepth(4);

    this.particleGraphics = this.add.graphics();
    this.particleGraphics.setDepth(8);
  }

  private initializeMagneticField(): void {
    if (this.levelSpecific?.magneticSources) {
      for (const source of this.levelSpecific.magneticSources) {
        this.emField.addMagneticSource({
          id: source.id,
          position: Vec2(source.position.x, source.position.y),
          strength: source.strength * this.fieldStrengthValue,
          radius: source.radius ?? 150,
        });
      }
    } else {
      this.emField.addMagneticSource({
        id: 'magnetic-center',
        position: Vec2(400, 300),
        strength: this.fieldStrengthValue,
        radius: 150,
      });
    }
  }

  private createFieldRenderer(): void {
    this.fieldRenderer = new FieldRenderer(this, this.emField, {
      magneticFieldColor: 0x9b59b6,
      lineWidth: 2,
      fieldLineCount: 8,
    }, GAME_HEIGHT, 1);
    this.fieldRenderer.render();
  }

  private createUI(): void {
    this.add.text(GAME_WIDTH / 2, 50, '洛伦兹力公式：F = qv×B', {
      fontSize: '24px',
      color: '#ffffff',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    this.hintText = this.add.text(GAME_WIDTH / 2, 85,
      this.levelSpecific?.hint ?? '调节磁场强度和初速度，让粒子偏转到目标区域', {
      fontSize: '16px',
      color: '#888888',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    this.createVelocityInput();
    this.createFieldStrengthInput();
    this.createStartButton();

    this.timerText = this.add.text(GAME_WIDTH / 2, 520, '时间: 0.00 秒', {
      fontSize: '20px',
      color: '#ffffff',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    this.resultText = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2, '', {
      fontSize: '32px',
      color: '#4ade80',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5).setVisible(false);
  }

  private createVelocityInput(): void {
    const inputBg = this.add.graphics();
    inputBg.fillStyle(0x2a2a4a, 1);
    inputBg.fillRect(150, 450, 200, 40);
    inputBg.lineStyle(2, 0x4a4a6a);
    inputBg.strokeRect(150, 450, 200, 40);

    this.add.text(150, 430, '初速度 v (单位/秒):', {
      fontSize: '14px',
      color: '#888888',
      fontFamily: 'Arial',
    });

    this.velocityInput = this.add.text(250, 470, `${this.velocityValue}`, {
      fontSize: '20px',
      color: '#ffffff',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    this.createButton(370, 470, '+', () => {
      if (this.gameState === 'idle') {
        this.velocityValue = Math.min(200, this.velocityValue + 10);
        this.updateVelocityDisplay();
      }
    });

    this.createButton(130, 470, '-', () => {
      if (this.gameState === 'idle') {
        this.velocityValue = Math.max(50, this.velocityValue - 10);
        this.updateVelocityDisplay();
      }
    });
  }

  private createFieldStrengthInput(): void {
    const inputBg = this.add.graphics();
    inputBg.fillStyle(0x2a2a4a, 1);
    inputBg.fillRect(450, 450, 200, 40);
    inputBg.lineStyle(2, 0x4a4a6a);
    inputBg.strokeRect(450, 450, 200, 40);

    this.add.text(450, 430, '磁场强度 B:', {
      fontSize: '14px',
      color: '#888888',
      fontFamily: 'Arial',
    });

    this.fieldStrengthInput = this.add.text(550, 470, `${this.fieldStrengthValue.toFixed(1)}`, {
      fontSize: '20px',
      color: '#ffffff',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    this.createButton(670, 470, '+', () => {
      if (this.gameState === 'idle') {
        this.fieldStrengthValue = Math.min(3.0, this.fieldStrengthValue + 0.2);
        this.updateFieldStrengthDisplay();
        this.updateMagneticField();
      }
    });

    this.createButton(430, 470, '-', () => {
      if (this.gameState === 'idle') {
        this.fieldStrengthValue = Math.max(0.2, this.fieldStrengthValue - 0.2);
        this.updateFieldStrengthDisplay();
        this.updateMagneticField();
      }
    });
  }

  private createButton(x: number, y: number, label: string, callback: () => void): Phaser.GameObjects.Graphics {
    const btn = this.add.graphics();
    btn.fillStyle(0x4a4a6a, 1);
    btn.fillRect(x - 25, y - 15, 50, 30);
    btn.lineStyle(1, 0x6a6a8a);
    btn.strokeRect(x - 25, y - 15, 50, 30);

    this.add.text(x, y, label, {
      fontSize: '18px',
      color: '#ffffff',
      fontFamily: 'Arial',
    }).setOrigin(0.5);

    btn.setInteractive(
      new Phaser.Geom.Rectangle(x - 25, y - 15, 50, 30),
      Phaser.Geom.Rectangle.Contains
    );
    btn.on('pointerdown', callback);

    btn.on('pointerover', () => {
      btn.clear();
      btn.fillStyle(0x5a5a7a, 1);
      btn.fillRect(x - 25, y - 15, 50, 30);
      btn.strokeRect(x - 25, y - 15, 50, 30);
    });

    btn.on('pointerout', () => {
      btn.clear();
      btn.fillStyle(0x4a4a6a, 1);
      btn.fillRect(x - 25, y - 15, 50, 30);
      btn.strokeRect(x - 25, y - 15, 50, 30);
    });

    return btn;
  }

  private createStartButton(): void {
    this.startButton = this.add.graphics();
    this.startButton.fillStyle(0x4ade80, 1);
    this.startButton.fillRect(680, 450, 100, 50);
    this.startButton.lineStyle(2, 0x4ade80);
    this.startButton.strokeRect(680, 450, 100, 50);

    this.add.text(730, 475, '发射', {
      fontSize: '20px',
      color: '#ffffff',
      fontFamily: 'Arial',
      fontStyle: 'bold',
    }).setOrigin(0.5);

    this.startButton.setInteractive(
      new Phaser.Geom.Rectangle(680, 450, 100, 50),
      Phaser.Geom.Rectangle.Contains
    );
    this.startButton.on('pointerdown', () => this.startGame());

    this.startButton.on('pointerover', () => {
      this.startButton?.clear();
      this.startButton?.fillStyle(0x5aee90, 1);
      this.startButton?.fillRect(680, 450, 100, 50);
      this.startButton?.strokeRect(680, 450, 100, 50);
    });

    this.startButton.on('pointerout', () => {
      this.startButton?.clear();
      this.startButton?.fillStyle(0x4ade80, 1);
      this.startButton?.fillRect(680, 450, 100, 50);
      this.startButton?.strokeRect(680, 450, 100, 50);
    });
  }

  private setupInputEvents(): void {
    this.input.keyboard?.on('keydown', (event: KeyboardEvent) => {
      if (this.gameState !== 'idle') return;

      if (event.key === 'Enter') {
        this.startGame();
      }

      if (event.key === 'r' || event.key === 'R') {
        this.resetGame();
      }
    });
  }

  private updateVelocityDisplay(): void {
    if (this.velocityInput) {
      this.velocityInput.setText(`${this.velocityValue}`);
    }
    this.updateHintText();
  }

  private updateFieldStrengthDisplay(): void {
    if (this.fieldStrengthInput) {
      this.fieldStrengthInput.setText(`${this.fieldStrengthValue.toFixed(1)}`);
    }
    this.updateHintText();
  }

  private updateMagneticField(): void {
    this.emField.clearMagneticSources();
    this.initializeMagneticField();
    this.fieldRenderer?.render();
  }

  private updateHintText(): void {
    const radius = (this.levelSpecific?.particleCharge ?? 1) * this.velocityValue /
                   (this.levelSpecific?.particleMass ?? 1) / this.fieldStrengthValue;
    this.hintText?.setText(
      `预计偏转半径 ≈ ${radius.toFixed(0)} 单位 | v=${this.velocityValue}, B=${this.fieldStrengthValue.toFixed(1)}`
    );
  }

  private updateTimerDisplay(): void {
    if (this.timerText) {
      this.timerText.setText(`时间: ${this.elapsedTime.toFixed(2)} 秒`);
    }
  }

  private startGame(): void {
    if (this.gameState !== 'idle') return;

    this.gameState = 'running';
    this.elapsedTime = 0;
    this.trajectoryHistory = [];

    const charge = this.levelSpecific?.particleCharge ?? 1;
    const mass = this.levelSpecific?.particleMass ?? 1;
    const startX = this.levelConfig?.player?.startPosition?.x ?? 100;
    const startY = this.levelConfig?.player?.startPosition?.y ?? 300;

    this.particle = {
      id: 'player-particle',
      position: Vec2(startX, GAME_HEIGHT - startY),
      velocity: Vec2(this.velocityValue, 0),
      charge: charge,
      mass: mass,
    };

    this.trajectoryHistory.push({ x: startX, y: startY });

    if (this.trajectoryGraphics) {
      this.trajectoryGraphics.clear();
    }

    this.resultText?.setVisible(false);
    this.updateStartButtonState('running');
  }

  private updateParticlePhysics(dt: number): void {
    if (!this.particle) return;

    const steps = 4;
    const subDt = dt / steps;

    for (let i = 0; i < steps; i++) {
      const force = this.emField.calculateLorentzForce(this.particle);

      const ax = force.x / this.particle.mass;
      const ay = force.y / this.particle.mass;

      this.particle.velocity.x += ax * subDt;
      this.particle.velocity.y += ay * subDt;

      this.particle.position.x += this.particle.velocity.x * subDt;
      this.particle.position.y += this.particle.velocity.y * subDt;
    }

    const screenX = this.particle.position.x;
    const screenY = GAME_HEIGHT - this.particle.position.y;

    if (this.trajectoryHistory.length < 500) {
      this.trajectoryHistory.push({ x: screenX, y: screenY });
    }
  }

  private renderParticle(): void {
    if (!this.particle || !this.particleGraphics) return;

    const screenX = this.particle.position.x;
    const screenY = GAME_HEIGHT - this.particle.position.y;

    this.particleGraphics.clear();

    const color = this.particle.charge > 0 ? 0xff6b6b : 0x4ecdc4;
    this.particleGraphics.fillStyle(color, 1);
    this.particleGraphics.fillCircle(screenX, screenY, 10);
    this.particleGraphics.lineStyle(2, 0xffffff, 1);
    this.particleGraphics.strokeCircle(screenX, screenY, 10);

    this.drawVelocityArrow(screenX, screenY);
  }

  private drawVelocityArrow(x: number, y: number): void {
    if (!this.particle || !this.particleGraphics) return;

    const vx = this.particle.velocity.x;
    const vy = -this.particle.velocity.y;
    const scale = 0.3;

    this.particleGraphics.lineStyle(2, 0xffff00, 1);
    this.particleGraphics.beginPath();
    this.particleGraphics.moveTo(x, y);
    this.particleGraphics.lineTo(x + vx * scale, y + vy * scale);
    this.particleGraphics.strokePath();
  }

  private drawTrajectory(): void {
    if (!this.trajectoryGraphics || this.trajectoryHistory.length < 2) return;

    this.trajectoryGraphics.clear();
    this.trajectoryGraphics.lineStyle(2, 0xffff00, 0.6);
    this.trajectoryGraphics.beginPath();

    const first = this.trajectoryHistory[0]!;
    this.trajectoryGraphics.moveTo(first.x, first.y);

    for (let i = 1; i < this.trajectoryHistory.length; i++) {
      const point = this.trajectoryHistory[i]!;
      this.trajectoryGraphics.lineTo(point.x, point.y);
    }

    this.trajectoryGraphics.strokePath();
  }

  private updateStartButtonState(state: GameState): void {
    if (!this.startButton) return;

    this.startButton.clear();

    if (state === 'running') {
      this.startButton.fillStyle(0x6a6a8a, 1);
      this.startButton.fillRect(680, 450, 100, 50);
    } else if (state === 'idle') {
      this.startButton.fillStyle(0x4ade80, 1);
      this.startButton.fillRect(680, 450, 100, 50);
    }
    this.startButton.strokeRect(680, 450, 100, 50);
  }

  private checkGameEnd(): void {
    if (!this.particle) return;

    const screenX = this.particle.position.x;
    const screenY = GAME_HEIGHT - this.particle.position.y;

    if (screenX >= 620 && screenX <= 680 && screenY >= 270 && screenY <= 330) {
      this.gameState = 'success';
      this.showSuccessResult();
      this.updateStartButtonState('idle');
    }

    if (screenX < 0 || screenX > GAME_WIDTH || screenY < 0 || screenY > GAME_HEIGHT) {
      this.gameState = 'failed';
      this.showFailResult('粒子超出边界！');
      this.updateStartButtonState('idle');
    }
  }

  private showSuccessResult(): void {
    const gameState = getGameState();
    gameState.completeLevel(7, this.elapsedTime);

    this.resultText?.setText(`🎉 恭喜！用时 ${this.elapsedTime.toFixed(2)}秒`)
      .setColor('#4ade80')
      .setVisible(true);

    this.hintText?.setText(
      `洛伦兹力使粒子偏转！F = qv×B，力垂直于速度方向`
    );

    this.createCompletionButtons();
  }

  private createCompletionButtons(): void {
    const y = GAME_HEIGHT / 2 + 80;

    const backBtn = this.add.text(GAME_WIDTH / 2, y, '返回关卡选择', {
      fontSize: '18px',
      color: '#4ade80',
      fontFamily: 'Arial',
      backgroundColor: '#333333',
      padding: { x: 15, y: 8 },
    }).setOrigin(0.5).setInteractive();

    backBtn.on('pointerdown', () => {
      this.scene.start('LevelSelectScene');
    });
  }

  private showFailResult(reason: string): void {
    this.resultText?.setText(`❌ ${reason}`)
      .setColor('#ff6b6b')
      .setVisible(true);

    this.hintText?.setText(
      `调整磁场强度或初速度，让粒子轨迹经过目标区域`
    );
  }

  private resetGame(): void {
    this.gameState = 'idle';
    this.elapsedTime = 0;
    this.particle = null;
    this.trajectoryHistory = [];

    if (this.trajectoryGraphics) {
      this.trajectoryGraphics.clear();
    }

    if (this.particleGraphics) {
      this.particleGraphics.clear();
    }

    this.resultText?.setVisible(false);
    this.updateTimerDisplay();
    this.updateHintText();
    this.updateStartButtonState('idle');
  }

  private showTutorial(): void {
    const tutorialData = getTutorial('level-7');
    if (!tutorialData) return;

    this.tutorialUI = new TutorialUI(this, {
      levelNumber: 7,
      tutorialData,
      onStart: () => {
        this.tutorialUI?.destroy();
        this.tutorialUI = null;
      },
      onSkip: () => {
        this.tutorialUI?.destroy();
        this.tutorialUI = null;
      },
    });
  }

  getGameState(): GameState {
    return this.gameState;
  }

  getElapsedTime(): number {
    return this.elapsedTime;
  }

  getParticle(): ChargedParticle | null {
    return this.particle;
  }
}